import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { KineticHero } from '../components/KineticHero';
import { EditorialMarquee } from '../components/EditorialMarquee';
import { DarkTexture } from '../components/DarkTexture';
import { BTN_PRIMARY_GLOW, BTN_OUTLINE_GLOW } from '../lib/buttonStyles';
import { ScrollReveal, StaggerReveal, StaggerItem } from '../components/ScrollReveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { actChapters, actMeta } from '../data/actContent';
import { enforcementTimeline } from '../data/rulesContent';
import { penalties } from '../data/penalties';
import { blogPosts } from '../data/blogPosts';
import { faqs } from '../data/faqs';

const spotlightImg = 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200';

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const Home = () => {
  const posts = blogPosts;

  return (
  <div>
    <SEO
      title="DPDPA — Digital Personal Data Protection Act 2023 & DPDP Rules 2025 Explained"
      description="Complete guide to India's DPDPA 2023 and DPDP Rules 2025 — sections, penalties, deadlines, consent requirements, compliance guides and templates. The clearest DPDPA resource online."
      jsonLd={faqJsonLd}
    />
    <KineticHero />
    <EditorialMarquee />

    {/* Manifesto Chapters */}
    <section className="relative bg-navy py-24 sm:py-32 px-6 sm:px-10 lg:px-16 overflow-hidden" data-testid="home-act-section">
      <DarkTexture orbs={false} />
      <div className="relative max-w-7xl mx-auto">
        <ScrollReveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan mb-4">The statute · {actMeta.chapters} chapters · {actMeta.sections} sections</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-6 max-w-3xl">The DPDPA, 2023 at a glance</h2>
          <p className="text-white/60 max-w-2xl text-base sm:text-lg leading-relaxed mb-16">The Act's structure follows the lifecycle of personal data: who it applies to, what organisations owe, what individuals can demand, and how the law is enforced.</p>
        </ScrollReveal>

        <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {actChapters.map((ch) => (
            <StaggerItem key={ch.number}>
              <Link
                to={`/dpdpa-act/section/${ch.sections[0]?.id}`}
                data-testid={`home-chapter-card-${ch.number}`}
                className="group block h-full bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-2xl hover:bg-white/10 hover:border-cyan/30 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="font-serif text-5xl text-white/10 group-hover:text-cyan/60 transition-colors">{ch.number}</span>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-white/30 mt-4">{ch.chapter} · {ch.range}</p>
                <h3 className="font-serif text-xl text-white mt-2 mb-2">{ch.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{ch.description}</p>
              </Link>
            </StaggerItem>
          ))}
        </StaggerReveal>
      </div>
    </section>

    {/* Enforcement Timeline */}
    <section className="bg-void py-20 px-6 sm:px-10 lg:px-16 border-t border-white/5" data-testid="home-timeline-section">
      <div className="max-w-5xl mx-auto">
        <ScrollReveal><h2 className="font-serif text-3xl sm:text-4xl text-white mb-12">Enforcement milestones</h2></ScrollReveal>
        <div className="space-y-0">
          {enforcementTimeline.map((m, i) => (
            <ScrollReveal key={m.date} delay={i * 0.1}>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-10 py-6 border-b border-white/10">
                <p className="font-mono text-sm text-cyan w-40 shrink-0">{m.date}</p>
                <div>
                  <p className="font-serif text-lg text-white mb-1">{m.title}</p>
                  <p className="text-sm text-white/50 leading-relaxed">{m.description}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>

    {/* Clipped Frame Spotlight - Compliance Guides / Templates */}
    <section className="relative bg-navy py-24 sm:py-32 px-6 sm:px-10 lg:px-16 overflow-hidden" data-testid="home-spotlight-section">
      <DarkTexture orbs={false} />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <ScrollReveal>
          <div className="relative">
            <div
              className="relative h-[420px] w-full overflow-hidden"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% 88%, 92% 100%, 0 100%)' }}
            >
              <img src={spotlightImg} alt="Compliance documentation" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-navy/30" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-surface/70 backdrop-blur-xl border border-cyan/30 text-cyan px-6 py-4 font-mono text-xs uppercase tracking-wide">
              6 Guides · 6 Templates
            </div>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan mb-4">Compliance Guides &amp; Templates</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight mb-6">From legal text to an audit-ready operation.</h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-8">
            Step-by-step playbooks for DPIAs, breach response, RoPA building and cross-border transfers — plus ready-to-use templates for notices, consent checklists and rights-request responses.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/compliance-guides" data-testid="home-cta-guides" className={BTN_PRIMARY_GLOW}>
              Explore Compliance Guides <ArrowRight size={16} />
            </Link>
            <Link to="/templates" data-testid="home-cta-templates" className={BTN_OUTLINE_GLOW}>
              Browse Templates
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>

    {/* Penalties teaser */}
    <section className="bg-void py-24 px-6 sm:px-10 lg:px-16" data-testid="home-penalties-section">
      <div className="max-w-5xl mx-auto">
        <ScrollReveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan mb-4">The Schedule · Section 33(1)</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight mb-6">Penalties for non-compliance</h2>
          <p className="text-white/60 max-w-2xl text-base sm:text-lg leading-relaxed mb-12">The 18-month window before May 2027 is a compliance sprint, not a grace period.</p>
        </ScrollReveal>
        <div className="border border-white/10 divide-y divide-white/10 bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden">
          {penalties.slice(0, 4).map((p, i) => (
            <ScrollReveal key={p.section} delay={i * 0.05}>
              <div className="flex items-center justify-between gap-4 px-6 py-5">
                <p className="text-sm text-white/70 max-w-md">{p.provision}</p>
                <p className="font-serif text-xl sm:text-2xl text-white whitespace-nowrap">{p.amount}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <Link to="/dpdpa-act" data-testid="home-cta-full-penalties" className="inline-flex items-center gap-2 mt-8 text-white font-medium hover:text-cyan transition-colors">
          See the full penalty schedule <ArrowRight size={16} />
        </Link>
      </div>
    </section>

    {/* Blog Grid */}
    <section className="bg-navy py-24 px-6 sm:px-10 lg:px-16 border-t border-white/5" data-testid="home-blog-section">
      <div className="max-w-7xl mx-auto">
        <ScrollReveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight">Latest on the DPDPA</h2>
            <Link to="/blog" data-testid="home-cta-view-blog" className="flex items-center gap-2 text-white font-medium hover:text-cyan transition-colors">
              View all articles <ArrowRight size={16} />
            </Link>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.slice(0, 3).map((post, i) => (
            <ScrollReveal key={post.slug} delay={i * 0.1}>
              <Link to={`/blog/${post.slug}`} data-testid={`home-blog-card-${post.slug}`} className="block group bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-5 hover:border-cyan/30 hover:-translate-y-1 transition-all duration-300 h-full">
                <p className="font-mono text-xs uppercase tracking-widest text-cyan mb-3">{post.tag}</p>
                <h3 className="font-serif text-2xl text-white mb-3 group-hover:text-cyan transition-colors leading-snug">{post.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed mb-3">{post.excerpt}</p>
                <span className="text-sm text-white font-medium flex items-center gap-1">Read article <ArrowUpRight size={13} /></span>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="bg-void py-24 px-6 sm:px-10 lg:px-16 border-t border-white/5" data-testid="home-faq-section">
      <div className="max-w-3xl mx-auto">
        <ScrollReveal><h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight mb-12">DPDPA, answered</h2></ScrollReveal>
        <Accordion type="single" collapsible>
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="font-serif text-lg text-white py-5 hover:text-cyan">{f.q}</AccordionTrigger>
              <AccordionContent className="text-white/60 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    {/* Certification & Compliance Suite */}
    <section className="bg-navy py-24 px-6 sm:px-10 lg:px-16 border-t border-white/5" data-testid="home-ecosystem-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 sm:p-10 flex flex-col hover:border-cyan/30 transition-colors duration-300" data-testid="home-certification-card">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan mb-4">Build In-House Expertise</p>
          <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 leading-snug">Looking for a DPDPA Certification?</h3>
          <p className="text-white/60 leading-relaxed mb-8 flex-1">
            Build practitioner-grade expertise with a hands-on training programme covering the Act, the Rules, DPIA methodology and consent architecture — designed for consultants, DPOs and compliance leads.
          </p>
          <a href="https://complyzen.in/academy" data-testid="home-cta-certification" className="inline-flex items-center gap-2 text-white font-semibold hover:text-cyan transition-colors w-fit">
            Explore Certifications <ArrowRight size={16} />
          </a>
        </div>
        <div className="relative bg-surface/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden p-8 sm:p-10 flex flex-col hover:border-magenta/30 transition-colors duration-300" data-testid="home-suite-card">
          <div className="relative">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-magenta mb-4">One Platform, Not a Patchwork</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 leading-snug">The Complete DPDPA Compliance Suite</h3>
            <p className="text-white/60 leading-relaxed mb-8">
              Several point solutions exist for individual pieces of DPDPA compliance — but this is the only end-to-end suite that manages consent, data discovery, DPIAs, rights requests, breach response and audit evidence in a single platform.
            </p>
            <a href="https://complyzen.in/products/zendpo" data-testid="home-cta-suite" className="inline-flex items-center gap-2 text-cyan font-semibold hover:text-white transition-colors w-fit">
              Explore the Compliance Suite <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Final CTA */}
    <section className="relative bg-navy py-24 px-6 sm:px-10 lg:px-16 text-center overflow-hidden" data-testid="home-final-cta-section">
      <DarkTexture />
      <ScrollReveal>
        <div className="relative">
          <h2 className="font-serif text-4xl sm:text-5xl text-white tracking-tight mb-6">Where does your organisation stand?</h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">Take our 4-step Compliance Assessment and get a clear read on your DPDPA exposure — free.</p>
          <Link to="/compliance-assessment" data-testid="home-final-cta-button" className={BTN_PRIMARY_GLOW + ' !px-8 !py-4'}>
            Start Free Assessment <ArrowRight size={16} />
          </Link>
        </div>
      </ScrollReveal>
    </section>
  </div>
  );
};

export default Home;
